import type { AnswerMap } from '../types/workflow'
import type { NimbusQuestionBuildInput } from '../lib/nimbus-payload-builder'

export interface NimbusStageDefinition {
  stage_id: string
  stage_name: string
  stage_order: number
  questionnaire: {
    title: string
    version: string
    description: string
    questionnaire_id: string
  }
  source_stage_ids: string[]
  buildQuestions: (answers: AnswerMap) => NimbusQuestionBuildInput[]
}

const VERSION = '1.0'

const SEX_LABELS: Record<string, string> = {
  male: 'Male',
  female: 'Female',
}

const GOAL_LABELS: Record<string, string> = {
  lose_weight: 'Lose weight',
  more_energy: 'Feel more energy',
  health_markers: 'Improve health markers',
  confidence: 'Build confidence',
  mobility: 'Better mobility',
  sleep: 'Sleep better',
}

const GLP1_EXPERIENCE_LABELS: Record<string, string> = {
  currently_taking: "Yes, I'm currently on one",
  took_in_past: 'Yes, but I stopped',
  first_time: 'No, this would be my first time',
  not_sure: "Honestly, I'm not sure",
}

const WHICH_MEDICATION_LABELS: Record<string, string> = {
  ozempic: 'Ozempic',
  wegovy: 'Wegovy',
  mounjaro: 'Mounjaro',
  zepbound: 'Zepbound',
  compounded_semaglutide: 'Compounded Semaglutide [CarniSema]',
  compounded_tirzepatide: 'Compounded Tirzepatide [CarniTide]',
  other: 'Other / Not sure',
}

const MEDICAL_CONDITION_LABELS: Record<string, string> = {
  type2_diabetes: 'Type 2 Diabetes',
  hypertension: 'Hypertension (High Blood Pressure)',
  heart_disease: 'Heart Disease',
  sleep_apnea: 'Sleep Apnea',
  pcos: 'PCOS (Polycystic Ovary Syndrome)',
  fatty_liver: 'Fatty Liver Disease (NAFLD)',
  depression: 'Depression / Anxiety',
  joint_pain: 'Joint Pain / Arthritis',
  kidney_disease: 'Kidney Disease',
  none: 'None of the above',
}

const LAST_DOSE_LABELS: Record<string, string> = {
  within_week: 'Within the past week',
  '1_4_weeks': '1-4 weeks ago',
  '1_3_months': '1-3 months ago',
  '3_6_months': '3-6 months ago',
  more_6_months: 'More than 6 months ago',
  not_sure: "Hmm, I'm not sure",
  less_than_month: 'Less than a month',
  '1_to_3_months': '1-3 months',
  '3_to_6_months': '3-6 months',
  '6_to_12_months': '6-12 months',
  over_year: 'Over a year',
}

const EXPERIENCE_REASON_LABELS: Record<string, string> = {
  side_effects: 'Side effects',
  cost: 'Cost',
  not_effective: "Wasn't effective",
  doctor_advised: 'Doctor advised',
  other: 'Other',
}

const EXPERIENCE_TOLERABILITY_LABELS: Record<string, string> = {
  very_well: 'Very well',
  manageable: 'Manageable',
  significant_sides: 'Significant side effects',
}

const EXPERIENCE_EFFECTIVENESS_LABELS: Record<string, string> = {
  very_effective: 'Very effective',
  somewhat: 'Somewhat effective',
  not_effective: 'Not effective',
}

const SAFETY_QUESTION_META: Array<{
  key: string
  question_id: string
  text: string
  order: number
}> = [
  {
    key: 'bowel_obstruction',
    question_id: 'bowelIssues',
    text: 'Do you have a history of bowel obstruction?',
    order: 1,
  },
  {
    key: 'mtc_men2',
    question_id: 'thyroidCondition',
    text: 'Do you have a personal or family history of medullary thyroid carcinoma (MTC) or Multiple Endocrine Neoplasia syndrome type 2 (MEN 2)?',
    order: 2,
  },
  {
    key: 'pancreatitis',
    question_id: 'pancreatitis',
    text: 'Have you ever had pancreatitis?',
    order: 3,
  },
  {
    key: 'breastfeeding',
    question_id: 'breastfeeding',
    text: 'Are you currently breastfeeding?',
    order: 4,
  },
  {
    key: 'eating_disorder',
    question_id: 'eatingDisorder',
    text: 'Have you been diagnosed with an eating disorder?',
    order: 5,
  },
  {
    key: 'gastroparesis',
    question_id: 'gastroparesis',
    text: 'Have you been diagnosed with gastroparesis (delayed stomach emptying)?',
    order: 6,
  },
  {
    key: 'pregnant_planning',
    question_id: 'pregnant',
    text: 'Are you currently pregnant or planning to become pregnant?',
    order: 7,
  },
]

const MEDICATION_LABELS: Record<string, string> = {
  ozempic: 'Ozempic® (Semaglutide)',
  wegovy: 'Wegovy® (Semaglutide)',
  mounjaro: 'Mounjaro® (Tirzepatide)',
  zepbound: 'Zepbound® (Tirzepatide)',
  compounded_semaglutide: 'Compounded Semaglutide [CarniSema®]',
  compounded_tirzepatide: 'Compounded Tirzepatide [CarniTide®]',
  other: 'Other / Not sure',
}

function formatMedicationId(medication: string): string {
  return medication
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function getBmiValue(answers: AnswerMap, key: 'height_feet' | 'height_inches' | 'weight_lbs'): unknown {
  if (answers[key] !== undefined) return answers[key]
  const bmiData = answers.bmi_data
  if (bmiData && typeof bmiData === 'object' && !Array.isArray(bmiData)) {
    return (bmiData as Record<string, unknown>)[key]
  }
  return undefined
}

export const NIMBUS_PAYLOAD_STAGES: NimbusStageDefinition[] = [
  {
    stage_id: 'ww-eligibility',
    stage_name: 'Eligibility & Background',
    stage_order: 1,
    questionnaire: {
      title: 'Eligibility Screening',
      version: VERSION,
      description: 'Assessment for Eligibility & Background',
      questionnaire_id: 'ww-eligibility-v1',
    },
    source_stage_ids: ['ww-basics'],
    buildQuestions: (answers) => [
      {
        question_id: 'dateOfBirth',
        text: 'Date of Birth',
        group: 'Eligibility & Background',
        order: 1,
        question_type: 'text',
        rawValue: answers.date_of_birth,
      },
      {
        question_id: 'sexAtBirth',
        text: 'Sex at Birth',
        group: 'Eligibility & Background',
        order: 2,
        question_type: 'text',
        rawValue: answers.sex_at_birth,
        optionTextById: SEX_LABELS,
      },
      {
        question_id: 'age',
        text: 'Age',
        group: 'Eligibility & Background',
        order: 3,
        question_type: 'number',
        rawValue: answers.age,
      },
    ],
  },
  {
    stage_id: 'ww-goals-lifestyle',
    stage_name: 'Goals & Lifestyle',
    stage_order: 2,
    questionnaire: {
      title: 'Goals and Lifestyle Assessment',
      version: VERSION,
      description: 'Assessment for Goals & Lifestyle',
      questionnaire_id: 'ww-goals-lifestyle-v1',
    },
    source_stage_ids: ['ww-goals'],
    buildQuestions: (answers) => {
      const selectedGoals = Array.isArray(answers.goals_selected)
        ? (answers.goals_selected as string[])
        : (typeof answers.primary_goal === 'string' && answers.primary_goal ? [answers.primary_goal] : [])

      return [
        {
          question_id: 'goals',
          text: 'So, now tell me what brought you here today?',
          group: 'Goals',
          order: 1,
          question_type: 'multi_select',
          rawValue: selectedGoals,
          optionTextById: GOAL_LABELS,
        },
      ]
    },
  },
  {
    stage_id: 'ww-safety-medical',
    stage_name: 'Safety & Medical History',
    stage_order: 3,
    questionnaire: {
      title: 'Safety and Medical History',
      version: VERSION,
      description: 'Assessment for Safety & Medical History',
      questionnaire_id: 'ww-safety-medical-v1',
    },
    source_stage_ids: ['ww-glp1-experience'],
    buildQuestions: (answers) => [
      {
        question_id: 'glp1Experience',
        text: 'Have you tried a GLP-1 before?',
        group: 'GLP-1 Experience',
        order: 1,
        question_type: 'text',
        rawValue: answers.glp1_experience,
        optionTextById: GLP1_EXPERIENCE_LABELS,
      },
    ],
  },
  {
    stage_id: 'ww-medications',
    stage_name: 'Current Medications & Allergies',
    stage_order: 4,
    questionnaire: {
      title: 'Medications and Allergies',
      version: VERSION,
      description: 'Assessment for Current Medications & Allergies',
      questionnaire_id: 'ww-medications-v1',
    },
    source_stage_ids: ['ww-current-medications', 'ww-medication-allergies'],
    buildQuestions: (answers) => {
      const medicationData =
        answers.medication_data && typeof answers.medication_data === 'object' && !Array.isArray(answers.medication_data)
          ? (answers.medication_data as Record<string, unknown>)
          : {}
      const medications = Array.isArray(medicationData.medications)
        ? (medicationData.medications as string[])
        : []
      const selectedMedicationOptions = medications.map((medication) => ({
        id: medication,
        text: formatMedicationId(medication),
      }))

      return [
        {
          question_id: 'currentMedications',
          text: 'Are you taking any other medications right now?',
          group: 'Current Medications & Allergies',
          order: 1,
          question_type: 'multi_select',
          rawValue: medications,
          selectedOptionPairs: selectedMedicationOptions,
          treatEmptyAsAnswered: true,
        },
        {
          question_id: 'medicationAllergies',
          text: 'Any allergies to medications I should know about?',
          group: 'Medications',
          order: 2,
          question_type: 'text',
          rawValue: answers.medication_allergies,
        },
      ]
    },
  },
  {
    stage_id: 'ww-body_metrics',
    stage_name: 'Body Metrics',
    stage_order: 104,
    questionnaire: {
      title: 'Body Metrics',
      version: VERSION,
      description: 'Additional assessment: body_metrics',
      questionnaire_id: 'ww-body_metrics-v1',
    },
    source_stage_ids: ['ww-bmi-calculator'],
    buildQuestions: (answers) => [
      {
        question_id: 'heightInches',
        text: 'Height (inches)',
        group: 'body_metrics',
        order: 1,
        question_type: 'text',
        rawValue: getBmiValue(answers, 'height_inches'),
      },
      {
        question_id: 'heightFeet',
        text: 'Height (feet)',
        group: 'body_metrics',
        order: 2,
        question_type: 'text',
        rawValue: getBmiValue(answers, 'height_feet'),
      },
      {
        question_id: 'currentWeight',
        text: 'Current Weight (lbs)',
        group: 'body_metrics',
        order: 3,
        question_type: 'text',
        rawValue: getBmiValue(answers, 'weight_lbs'),
      },
    ],
  },
  {
    stage_id: 'ww-body_photo',
    stage_name: 'Body Photo',
    stage_order: 105,
    questionnaire: {
      title: 'Body Photo',
      version: VERSION,
      description: 'Additional assessment: body_photo',
      questionnaire_id: 'ww-body_photo-v1',
    },
    source_stage_ids: ['ww-body-photo'],
    buildQuestions: (answers) => [
      {
        question_id: 'bodyPhotoS3Key',
        text: 'One more thing — can you share a full body photo?',
        group: 'body_photo',
        order: 1,
        question_type: 'text',
        rawValue: answers.body_photo_key,
      },
    ],
  },
  {
    stage_id: 'ww-dosage',
    stage_name: 'Dosage',
    stage_order: 107,
    questionnaire: {
      title: 'Dosage',
      version: VERSION,
      description: 'Additional assessment: dosage',
      questionnaire_id: 'ww-dosage-v1',
    },
    source_stage_ids: ['ww-glp1-dosage'],
    buildQuestions: (answers) => {
      const dosageData =
        answers.glp1_dosages && typeof answers.glp1_dosages === 'object' && !Array.isArray(answers.glp1_dosages)
          ? (answers.glp1_dosages as Record<string, unknown>)
          : {}
      return Object.entries(dosageData).map(([medicationId, dosage], index) => ({
        question_id: medicationId,
        text: MEDICATION_LABELS[medicationId] || formatMedicationId(medicationId),
        group: 'dosage',
        order: index + 1,
        question_type: 'text',
        rawValue: dosage,
      }))
    },
  },
  {
    stage_id: 'ww-experience',
    stage_name: 'Experience',
    stage_order: 108,
    questionnaire: {
      title: 'Experience',
      version: VERSION,
      description: 'Additional assessment: experience',
      questionnaire_id: 'ww-experience-v1',
    },
    source_stage_ids: ['ww-glp1-experience-details'],
    buildQuestions: (answers) => {
      const experienceData =
        answers.experience_data && typeof answers.experience_data === 'object' && !Array.isArray(answers.experience_data)
          ? (answers.experience_data as Record<string, unknown>)
          : {}
      const effectiveness = experienceData.effectiveness || answers.effectiveness
      const whyStopped = experienceData.whyStopped || answers.whyStopped
      const tolerability = experienceData.tolerability || answers.tolerability

      return [
        {
          question_id: 'effectiveness',
          text: 'How effective was it?',
          group: 'experience',
          order: 1,
          question_type: 'text',
          rawValue: effectiveness,
          optionTextById: EXPERIENCE_EFFECTIVENESS_LABELS,
        },
        {
          question_id: 'whyStopped',
          text: 'Why did you stop?',
          group: 'experience',
          order: 2,
          question_type: 'text',
          rawValue: whyStopped,
          optionTextById: EXPERIENCE_REASON_LABELS,
        },
        {
          question_id: 'tolerability',
          text: 'How well did you tolerate it?',
          group: 'experience',
          order: 3,
          question_type: 'text',
          rawValue: tolerability,
          optionTextById: EXPERIENCE_TOLERABILITY_LABELS,
        },
      ]
    },
  },
  {
    stage_id: 'ww-health_conditions',
    stage_name: 'Health Conditions',
    stage_order: 109,
    questionnaire: {
      title: 'Health Conditions',
      version: VERSION,
      description: 'Additional assessment: health_conditions',
      questionnaire_id: 'ww-health_conditions-v1',
    },
    source_stage_ids: ['ww-medical-conditions'],
    buildQuestions: (answers) => [
      {
        question_id: 'healthConditions',
        text: 'Do any of these sound familiar?',
        group: 'health_conditions',
        order: 1,
        question_type: 'multi_select',
        rawValue: answers.medical_conditions,
        optionTextById: MEDICAL_CONDITION_LABELS,
      },
    ],
  },
  {
    stage_id: 'ww-id_upload',
    stage_name: 'Id Upload',
    stage_order: 110,
    questionnaire: {
      title: 'Id Upload',
      version: VERSION,
      description: 'Additional assessment: id_upload',
      questionnaire_id: 'ww-id_upload-v1',
    },
    source_stage_ids: ['ww-id-upload'],
    buildQuestions: (answers) => [
      {
        question_id: 'idPhotoS3Key',
        text: 'Can I see a quick photo of your ID?',
        group: 'id_upload',
        order: 1,
        question_type: 'text',
        rawValue: answers.id_photo_key,
      },
    ],
  },
  {
    stage_id: 'ww-last_dose',
    stage_name: 'Last Dose',
    stage_order: 111,
    questionnaire: {
      title: 'Last Dose',
      version: VERSION,
      description: 'Additional assessment: last_dose',
      questionnaire_id: 'ww-last_dose-v1',
    },
    source_stage_ids: ['ww-current-glp1-details', 'ww-past-glp1'],
    buildQuestions: (answers) => {
      const isCurrent = typeof answers.glp1_duration === 'string' && answers.glp1_duration
      const value = isCurrent ? answers.glp1_duration : answers.glp1_last_dose_timing
      const text = isCurrent ? 'How long have you been on it?' : 'When did you take your last dose?'
      return [
        {
          question_id: 'lastDose',
          text,
          group: 'last_dose',
          order: 1,
          question_type: 'text',
          rawValue: value,
          optionTextById: LAST_DOSE_LABELS,
        },
      ]
    },
  },
  {
    stage_id: 'ww-plan_selection',
    stage_name: 'Plan Selection',
    stage_order: 112,
    questionnaire: {
      title: 'Plan Selection',
      version: VERSION,
      description: 'Additional assessment: plan_selection',
      questionnaire_id: 'ww-plan_selection-v1',
    },
    source_stage_ids: ['ww-plan-selection'],
    buildQuestions: (answers) => {
      const selection =
        answers.product_selection && typeof answers.product_selection === 'object' && !Array.isArray(answers.product_selection)
          ? (answers.product_selection as Record<string, unknown>)
          : {}
      const selectedPlan = (selection.product as string | undefined) || (answers.product as string | undefined)
      return [
        {
          question_id: 'selectedPlan',
          text: 'Selected Plan',
          group: 'plan_selection',
          order: 1,
          question_type: 'text',
          rawValue: selectedPlan,
        },
      ]
    },
  },
  {
    stage_id: 'ww-safety_questions',
    stage_name: 'Safety Questions',
    stage_order: 113,
    questionnaire: {
      title: 'Safety Questions',
      version: VERSION,
      description: 'Additional assessment: safety_questions',
      questionnaire_id: 'ww-safety_questions-v1',
    },
    source_stage_ids: ['ww-safety-screening'],
    buildQuestions: (answers) => {
      const safetyAnswers =
        answers.safety_answers && typeof answers.safety_answers === 'object' && !Array.isArray(answers.safety_answers)
          ? (answers.safety_answers as Record<string, unknown>)
          : {}

      return SAFETY_QUESTION_META.map((question) => ({
        question_id: question.question_id,
        text: question.text,
        group: 'safety_questions',
        order: question.order,
        question_type: 'text' as const,
        rawValue: safetyAnswers[question.key],
      }))
    },
  },
  {
    stage_id: 'ww-which_medications',
    stage_name: 'Which Medications',
    stage_order: 114,
    questionnaire: {
      title: 'Which Medications',
      version: VERSION,
      description: 'Additional assessment: which_medications',
      questionnaire_id: 'ww-which_medications-v1',
    },
    source_stage_ids: ['ww-current-glp1-medication'],
    buildQuestions: (answers) => [
      {
        question_id: 'previousMedications',
        text: 'Got it — which ones have you tried?',
        group: 'which_medications',
        order: 1,
        question_type: 'multi_select',
        rawValue: answers.current_glp1_medications,
        optionTextById: WHICH_MEDICATION_LABELS,
      },
    ],
  },
  {
    stage_id: 'ww-shipping_billing',
    stage_name: 'Shipping & Billing Address',
    stage_order: 115,
    questionnaire: {
      title: 'Shipping & Billing Address',
      version: VERSION,
      description: 'Shipping and billing address for medication delivery',
      questionnaire_id: 'ww-shipping_billing-v1',
    },
    source_stage_ids: ['ww-shipping-billing'],
    buildQuestions: (answers) => {
      const data =
        answers.shipping_billing_data && typeof answers.shipping_billing_data === 'object' && !Array.isArray(answers.shipping_billing_data)
          ? (answers.shipping_billing_data as Record<string, unknown>)
          : {}
      return [
        {
          question_id: 'shippingAddressLine1',
          text: 'Shipping Street Address',
          group: 'shipping_billing',
          order: 1,
          question_type: 'text' as const,
          rawValue: data.shippingAddressLine1,
        },
        {
          question_id: 'shippingAddressLine2',
          text: 'Shipping Apt / Suite / Unit',
          group: 'shipping_billing',
          order: 2,
          question_type: 'text' as const,
          rawValue: data.shippingAddressLine2,
        },
        {
          question_id: 'shippingCity',
          text: 'Shipping City',
          group: 'shipping_billing',
          order: 3,
          question_type: 'text' as const,
          rawValue: data.shippingCity,
        },
        {
          question_id: 'shippingState',
          text: 'Shipping State',
          group: 'shipping_billing',
          order: 4,
          question_type: 'text' as const,
          rawValue: data.shippingState,
        },
        {
          question_id: 'shippingPostalCode',
          text: 'Shipping ZIP Code',
          group: 'shipping_billing',
          order: 5,
          question_type: 'text' as const,
          rawValue: data.shippingPostalCode,
        },
        {
          question_id: 'billingSameAsShipping',
          text: 'Billing same as shipping?',
          group: 'shipping_billing',
          order: 6,
          question_type: 'text' as const,
          rawValue: data.billingSameAsShipping,
        },
        {
          question_id: 'billingAddressLine1',
          text: 'Billing Street Address',
          group: 'shipping_billing',
          order: 7,
          question_type: 'text' as const,
          rawValue: data.billingAddressLine1,
        },
        {
          question_id: 'billingAddressLine2',
          text: 'Billing Apt / Suite / Unit',
          group: 'shipping_billing',
          order: 8,
          question_type: 'text' as const,
          rawValue: data.billingAddressLine2,
        },
        {
          question_id: 'billingCity',
          text: 'Billing City',
          group: 'shipping_billing',
          order: 9,
          question_type: 'text' as const,
          rawValue: data.billingCity,
        },
        {
          question_id: 'billingState',
          text: 'Billing State',
          group: 'shipping_billing',
          order: 10,
          question_type: 'text' as const,
          rawValue: data.billingState,
        },
        {
          question_id: 'billingPostalCode',
          text: 'Billing ZIP Code',
          group: 'shipping_billing',
          order: 11,
          question_type: 'text' as const,
          rawValue: data.billingPostalCode,
        },
      ]
    },
  },
]
